import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { PricingGrid, usePlans } from "../components/SubscriptionUI";
import { toast } from "sonner";
import { 
  Clock, 
  DollarSign, 
  Users, 
  Calendar, 
  TrendingUp, 
  Shield,
  ChevronRight,
  CheckCircle,
  Mail,
  CheckCircle2,
  PlayCircle,
  Eye,
  EyeOff,
  Lock,
  Smartphone
} from "lucide-react";

const Landing = () => {
  const navigate = useNavigate();
  const { user, loading, hydrateAfterLogin } = useAuth();
  // Live-priced plans from the Super Admin Pricing CMS (falls back to
  // the static PLANS constant during the initial fetch). The signup
  // plan picker below reads directly from here so a price change in
  // /admin/plans is reflected instantly on the signup dialog.
  const { plans: livePlans } = usePlans();
  const [authOpen, setAuthOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [magicEmail, setMagicEmail] = useState("");
  const [magicSending, setMagicSending] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [authError, setAuthError] = useState(null);

  // Password auth state (P1) — kept in Landing so the same dialog handles
  // both signup (owner flow: creates tenant) and sign-in.
  const [authTab, setAuthTab] = useState("signin"); // "signin" | "signup"
  const [pwdEmail, setPwdEmail] = useState("");
  const [pwdPassword, setPwdPassword] = useState("");
  const [pwdCompany, setPwdCompany] = useState("");
  const [pwdName, setPwdName] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [pwdSubmitting, setPwdSubmitting] = useState(false);
  // Plan the user picked on the signup form. Defaults to Free — if they
  // choose Starter/Pro we stash a `pending_checkout` before submit so the
  // App.js resume-checkout effect takes them straight to Stripe after
  // account creation. If they arrived from the PricingGrid on the landing
  // page (pendingCheckoutIntent already set), we hide the picker so we
  // don't double-ask.
  const [signupPlan, setSignupPlan] = useState("free");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // Phone OTP state (P3)
  const [phoneOpen, setPhoneOpen] = useState(false);   // reveals phone form
  const [phone, setPhone] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(true);

  // If user is already logged in, redirect to the right home (admin or app)
  useEffect(() => {
    if (!user) return;
    const home = user.is_super_admin && !user.impersonating ? '/admin' : '/dashboard';
    navigate(home, { replace: true });
  }, [user, navigate]);

  // Detect ?error=... from Google OAuth callback — surface a friendly banner
  // (with magic-link autofocused) instead of leaving the user on Google's
  // generic "Access blocked" screen if Google bounced the redirect.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get("error");
    if (!err) return;
    const friendly = {
      auth_failed: "Google sign-in is temporarily unavailable. Please use the email link below — it works the same way and signs you in with one click.",
      redirect_uri_mismatch: "Google sign-in is being repaired. Please use the email link below.",
      no_code: "Google sign-in didn't complete. Please try again or use the email link below.",
      no_email: "Google didn't return your email address. Please use the email link below instead.",
      userinfo_failed: "We couldn't load your Google profile. Please use the email link below.",
      server_error: "Something went wrong on our end. Please try the email link below.",
    }[err] || "Sign-in didn't complete. Please use the email link below.";
    setAuthError(friendly);
    setAuthOpen(true);
    // Clean the URL so a refresh doesn't keep showing the banner.
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  // Trusted-device silent sign-in (P3): if the user was here before and
  // opted to remember their device, they get straight into the app with no
  // OTP round-trip. Runs once on mount only when nothing else has set a
  // session_token yet. Skipped after an explicit sign-out for one tab
  // session so the user isn't yanked back in against their will.
  useEffect(() => {
    if (loading || user) return;
    if (localStorage.getItem("session_token")) return;
    if (sessionStorage.getItem("just_signed_out")) {
      sessionStorage.removeItem("just_signed_out");
      return;
    }
    const device = localStorage.getItem("device_token");
    if (!device) return;
    apiClient.post("/auth/otp/device-signin", { device_token: device })
      .then(async (res) => {
        localStorage.setItem("session_token", res.data.session_token);
        try {
          const me = await apiClient.get("/auth/me");
          await hydrateAfterLogin(me.data);
          navigate(res.data.redirect_to || "/dashboard", { replace: true });
        } catch {
          window.location.href = "/dashboard";
        }
      })
      .catch(() => {
        localStorage.removeItem("device_token");
      });
  }, [loading, user, navigate, hydrateAfterLogin]);

  // When an unauthenticated visitor clicks "Upgrade to Starter/Pro" on the
  // landing pricing grid, we redirect them here with `#signin` so we can
  // pop the sign-in dialog immediately. `App.js` then auto-resumes the
  // pending checkout after successful auth. We watch both the initial
  // mount AND subsequent hashchange events since the click happens after
  // Landing has already mounted (browser just updates the hash in place).
  const [pendingCheckoutIntent, setPendingCheckoutIntent] = useState(null);
  useEffect(() => {
    const openIfPending = () => {
      const stashed = localStorage.getItem("pending_checkout");
      if (window.location.hash === "#signin" && stashed) {
        try {
          setPendingCheckoutIntent(JSON.parse(stashed));
        } catch { /* ignore malformed */ }
        setAuthOpen(true);
        window.history.replaceState({}, "", window.location.pathname);
      }
    };
    openIfPending();
    window.addEventListener("hashchange", openIfPending);
    return () => window.removeEventListener("hashchange", openIfPending);
  }, []);

  const handleLogin = () => {
    // Use OUR own Google OAuth flow (routed through backend) instead of
    // auth.emergentagent.com — which returns raw 422 JSON when the user clicks
    // Cancel on the Google consent screen (we can't fix their backend). Our
    // /api/auth/google/callback handles access_denied by silently redirecting
    // to `/` so the cancel UX is clean.
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    const state = encodeURIComponent(window.location.origin);
    // Pre-fetching /auth/google/login gives us the consent URL with our
    // callback pre-filled; we pass state via query so the backend knows which
    // frontend origin to send the user back to after token exchange.
    window.location.href = `${apiBase}/api/auth/google/start?state=${state}`;
  };

  const handleMagicLink = async (e) => {
    e?.preventDefault?.();
    const email = magicEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setMagicSending(true);
    try {
      await apiClient.post("/auth/magic-link/request", { email });
      setMagicSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send sign-in email. Please try again.");
    } finally {
      setMagicSending(false);
    }
  };

  // ── Password auth handlers ──────────────────────────────────────────
  const applySessionAndGo = async (sessionToken, redirectTo) => {
    localStorage.setItem("session_token", sessionToken);
    try {
      const me = await apiClient.get("/auth/me");
      await hydrateAfterLogin(me.data);
      navigate(redirectTo || (me.data.is_super_admin ? "/admin" : "/dashboard"), { replace: true });
    } catch {
      // Fallback: force full reload so App.js re-checks auth cleanly.
      window.location.href = redirectTo || "/dashboard";
    }
  };

  const handlePasswordSignIn = async (e) => {
    e?.preventDefault?.();
    const email = pwdEmail.trim().toLowerCase();
    if (!email || !pwdPassword) {
      toast.error("Please enter your email and password.");
      return;
    }
    setPwdSubmitting(true);
    try {
      const res = await apiClient.post("/auth/login", { email, password: pwdPassword });
      await applySessionAndGo(res.data.session_token, res.data.redirect_to);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Invalid email or password.";
      toast.error(msg);
    } finally {
      setPwdSubmitting(false);
    }
  };

  const handlePasswordSignUp = async (e) => {
    e?.preventDefault?.();
    const email = pwdEmail.trim().toLowerCase();
    const company = pwdCompany.trim();
    if (!email || !pwdPassword || !company) {
      toast.error("Email, password and company name are required.");
      return;
    }
    if (pwdPassword.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    // If the user picked Starter/Pro on the signup form, stash a
    // pending_checkout so App.js redirects them to Stripe right after the
    // company setup completes. Don't clobber an intent already set by
    // clicking Upgrade on the PricingGrid.
    if (signupPlan !== "free" && !localStorage.getItem("pending_checkout")) {
      localStorage.setItem("pending_checkout", JSON.stringify({
        plan: signupPlan,
        billing_cycle: "monthly",
        discount_code: null,
        stashed_at: Date.now(),
      }));
    }
    setPwdSubmitting(true);
    try {
      const res = await apiClient.post("/auth/signup", {
        email,
        password: pwdPassword,
        name: pwdName.trim() || undefined,
        company_name: company,
      });
      toast.success("Account created — welcome to Nirvanta!");
      await applySessionAndGo(res.data.session_token, res.data.redirect_to);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d.msg).join(" ")
          : "Could not create account. Please try again.";
      toast.error(msg);
      // Roll back the stashed intent so the user isn't stuck in a
      // pending-checkout loop if signup failed.
      if (signupPlan !== "free") {
        localStorage.removeItem("pending_checkout");
      }
    } finally {
      setPwdSubmitting(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e?.preventDefault?.();
    const email = pwdEmail.trim().toLowerCase();
    if (!email) {
      toast.error("Enter your email first.");
      return;
    }
    setForgotSending(true);
    try {
      await apiClient.post("/auth/password/forgot", { email });
      setForgotSent(true);
    } catch {
      // Endpoint returns 200 even for unknown emails — show success anyway.
      setForgotSent(true);
    } finally {
      setForgotSending(false);
    }
  };

  // ── Phone OTP handlers (P3) ─────────────────────────────────────────
  const handleRequestOtp = async (e) => {
    e?.preventDefault?.();
    const p = phone.trim();
    if (!p) {
      toast.error("Enter your phone number.");
      return;
    }
    setOtpSubmitting(true);
    try {
      await apiClient.post("/auth/otp/request", { phone: p });
      setOtpSent(true);
      toast.success("If we have your number on file, we've texted you a 6-digit code.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send code. Please try again.");
    } finally {
      setOtpSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e?.preventDefault?.();
    if (otpCode.length < 4) {
      toast.error("Enter the code from your text message.");
      return;
    }
    setOtpSubmitting(true);
    try {
      const res = await apiClient.post("/auth/otp/verify", {
        phone: phone.trim(),
        code: otpCode.trim(),
        remember_device: rememberDevice,
      });
      if (res.data.device_token) {
        localStorage.setItem("device_token", res.data.device_token);
      }
      toast.success("Signed in!");
      await applySessionAndGo(res.data.session_token, res.data.redirect_to);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Incorrect code.");
    } finally {
      setOtpSubmitting(false);
    }
  };

  // Show minimal loader while checking auth (prevents landing page flash for returning users)
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A192F] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#0D9488] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const features = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Employee Management",
      description: "Track pay structures, hire dates, and employee status all in one place."
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Time Tracking",
      description: "Simple hourly time entry with automatic overtime calculation."
    },
    {
      icon: <DollarSign className="w-6 h-6" />,
      title: "Payroll Tracking",
      description: "Record payments, handle partial payments, and track outstanding balances."
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Scheduling",
      description: "Weekly scheduling with employee availability management."
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Real-time Insights",
      description: "Dashboard KPIs showing outstanding balances and payroll estimates."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Multi-tenant",
      description: "Secure data isolation for multiple businesses on one platform."
    }
  ];

  const benefits = [
    "Quick pay recording in under 3 clicks",
    "Support for hourly, salaried, and daily rate employees",
    "Partial payment tracking with balance visibility",
    "Employee availability and shift scheduling",
    "Export payment summaries as PDF"
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="bg-[#0A192F] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Navigation */}
          <nav className="flex items-center justify-between py-6">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#0D9488] rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold font-['Manrope'] tracking-tight">Nirvanta</span>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                onClick={() => setDemoOpen(true)}
                data-testid="nav-demo-btn"
                variant="outline"
                className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white font-semibold px-5"
              >
                Demo
              </Button>
              <Button 
                onClick={() => setAuthOpen(true)}
                data-testid="nav-signin-btn"
                className="bg-[#0D9488] hover:bg-[#14B8A6] text-white font-semibold px-5"
              >
                Sign In
              </Button>
            </div>
          </nav>

          {/* Hero Content */}
          <div className="py-20 sm:py-28">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 font-['Manrope']">
                Smart Workforce Pay & Scheduling
              </h1>
              <p className="text-lg sm:text-xl text-slate-300 mb-10">
                Simplify payroll tracking, manage employee schedules, and keep real-time visibility on your workforce costs. Built for restaurants, retail, and small businesses.
              </p>
              <Button
                onClick={() => { setAuthTab("signup"); setAuthOpen(true); }}
                data-testid="get-started-btn"
                className="bg-[#0D9488] hover:bg-[#14B8A6] text-white font-semibold px-10 py-6 text-lg h-auto"
              >
                Start Free Trial
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <p className="text-sm text-slate-400 mt-4">
                No credit card required · Free forever for up to 3 employees
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Auth dialog (triggered by both nav + hero CTAs) */}
      <Dialog open={authOpen} onOpenChange={(open) => {
        setAuthOpen(open);
        if (!open) {
          setMagicSent(false); setMagicEmail(""); setAuthError(null);
          setPwdEmail(""); setPwdPassword(""); setPwdCompany(""); setPwdName("");
          setShowPwd(false); setForgotOpen(false); setForgotSent(false);
          setAuthTab("signin");
          setSignupPlan("free");
          setPhoneOpen(false); setPhone(""); setOtpCode(""); setOtpSent(false);
        }
      }}>
        <DialogContent data-testid="auth-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold tracking-tight">
              {magicSent
                ? "Check your email"
                : forgotSent
                  ? "Reset link sent"
                  : pendingCheckoutIntent
                    ? `Sign in to continue to ${pendingCheckoutIntent.plan === "pro" ? "Pro" : "Starter"} checkout`
                    : "Get started with Nirvanta"}
            </DialogTitle>
            <DialogDescription>
              {magicSent
                ? <>We sent a sign-in link to <span className="font-medium text-slate-700">{magicEmail}</span>. It expires in 15 minutes.</>
                : forgotSent
                  ? <>If an account exists for <span className="font-medium text-slate-700">{pwdEmail}</span>, you&apos;ll receive a password-reset link within a minute. The link is valid for 60 minutes.</>
                  : pendingCheckoutIntent
                    ? <>After signing in and creating your company, we&apos;ll take you straight to Stripe to activate your <span className="font-semibold capitalize">{pendingCheckoutIntent.plan}</span> plan.</>
                    : "Sign in with your password, Google, or a magic link — whichever you prefer."}
            </DialogDescription>
          </DialogHeader>

          {authError && !magicSent && !forgotSent && (
            <div
              role="alert"
              data-testid="auth-error-banner"
              className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            >
              {authError}
            </div>
          )}

          {magicSent ? (
            <div className="text-center py-2 space-y-3" data-testid="magic-link-sent">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <button
                type="button"
                onClick={() => { setMagicSent(false); setMagicEmail(""); }}
                className="text-xs text-slate-500 hover:text-slate-700 underline"
                data-testid="magic-link-back-btn"
              >
                Use a different email
              </button>
            </div>
          ) : forgotSent ? (
            <div className="text-center py-2 space-y-3" data-testid="forgot-sent">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <button
                type="button"
                onClick={() => { setForgotSent(false); setForgotOpen(false); }}
                className="text-xs text-slate-500 hover:text-slate-700 underline"
                data-testid="forgot-back-btn"
              >
                Back to sign in
              </button>
            </div>
          ) : (
            <Tabs value={authTab} onValueChange={setAuthTab} className="pt-2">
              <TabsList
                className={`grid w-full grid-cols-2 ${phoneOpen ? "hidden" : ""}`}
                data-testid="auth-tabs"
              >
                <TabsTrigger value="signin" data-testid="auth-tab-signin">Sign in</TabsTrigger>
                <TabsTrigger value="signup" data-testid="auth-tab-signup">Create account</TabsTrigger>
              </TabsList>

              {/* ── Sign in tab ──────────────────────────────────────── */}
              <TabsContent value="signin" className="space-y-4 pt-4">
                {phoneOpen ? (
                  // Phone-only view — hide password/Google/magic-link so the
                  // user isn't confused by 4 sign-in choices when they've
                  // already committed to the phone flow.
                  <div className="pt-1" data-testid="phone-signin-fullscreen">
                    {otpSent ? (
                      <form onSubmit={handleVerifyOtp} className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-4" data-testid="phone-otp-panel">
                        <p className="text-sm text-slate-700">
                          We texted a 6-digit code to <span className="font-semibold">{phone}</span>.
                        </p>
                        <Input
                          type="text"
                          inputMode="numeric"
                          pattern="\d{6}"
                          maxLength={6}
                          placeholder="123456"
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                          disabled={otpSubmitting}
                          data-testid="phone-otp-code-input"
                          autoComplete="one-time-code"
                          className="text-center tracking-widest text-lg"
                          autoFocus
                        />
                        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={rememberDevice}
                            onChange={(e) => setRememberDevice(e.target.checked)}
                            className="accent-[#0D9488]"
                            data-testid="phone-otp-remember-input"
                          />
                          Trust this device for 60 days (skip SMS next time)
                        </label>
                        <div className="flex gap-2">
                          <Button
                            type="submit"
                            disabled={otpSubmitting || otpCode.length < 4}
                            data-testid="phone-otp-verify-btn"
                            className="flex-1 bg-[#0D9488] hover:bg-[#14B8A6] text-white py-3 h-auto"
                          >
                            {otpSubmitting ? "Verifying…" : "Verify & sign in"}
                          </Button>
                          <Button
                            type="button"
                            onClick={() => { setOtpSent(false); setOtpCode(""); }}
                            variant="outline"
                            data-testid="phone-otp-back-btn"
                          >
                            Change number
                          </Button>
                        </div>
                        <button
                          type="button"
                          onClick={() => { setPhoneOpen(false); setOtpSent(false); setPhone(""); setOtpCode(""); }}
                          className="w-full text-xs text-slate-500 hover:text-slate-700 underline pt-1"
                          data-testid="phone-signin-exit"
                        >
                          Use email instead
                        </button>
                      </form>
                    ) : (
                      <form onSubmit={handleRequestOtp} className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-4" data-testid="phone-request-panel">
                        <label className="block text-sm font-medium text-slate-700">
                          Sign in with your phone number
                        </label>
                        <Input
                          type="tel"
                          inputMode="tel"
                          placeholder="(555) 010-1234"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          disabled={otpSubmitting}
                          data-testid="phone-signin-input"
                          autoComplete="tel"
                          autoFocus
                          className="text-base"
                        />
                        <Button
                          type="submit"
                          disabled={otpSubmitting || !phone}
                          data-testid="phone-request-btn"
                          className="w-full bg-[#0D9488] hover:bg-[#14B8A6] text-white py-3 h-auto"
                        >
                          <Smartphone className="w-4 h-4 mr-2" />
                          {otpSubmitting ? "Sending code…" : "Text me a code"}
                        </Button>
                        <p className="text-[11px] text-slate-500 text-center leading-relaxed">
                          You must be a Nirvanta employee with a phone on file. Ask your manager if you can&apos;t sign in.
                        </p>
                        <button
                          type="button"
                          onClick={() => setPhoneOpen(false)}
                          className="w-full text-xs text-slate-500 hover:text-slate-700 underline"
                          data-testid="phone-signin-exit"
                        >
                          ← Back to all sign-in options
                        </button>
                      </form>
                    )}
                  </div>
                ) : (
                  <>
                <form onSubmit={handlePasswordSignIn} className="space-y-3" data-testid="signin-form">
                  <div className="space-y-1.5">
                    <label htmlFor="signin-email" className="block text-xs font-medium text-slate-600">Email</label>
                    <Input
                      id="signin-email"
                      type="email"
                      placeholder="you@company.com"
                      value={pwdEmail}
                      onChange={(e) => setPwdEmail(e.target.value)}
                      disabled={pwdSubmitting}
                      data-testid="signin-email-input"
                      autoComplete="email"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="signin-password" className="flex items-center justify-between text-xs font-medium text-slate-600">
                      <span>Password</span>
                      <button
                        type="button"
                        onClick={() => setShowPwd((v) => !v)}
                        className="text-slate-400 hover:text-slate-600 inline-flex items-center gap-1"
                        data-testid="signin-show-password"
                      >
                        {showPwd ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        {showPwd ? "Hide" : "Show"}
                      </button>
                    </label>
                    <Input
                      id="signin-password"
                      type={showPwd ? "text" : "password"}
                      placeholder="••••••••"
                      value={pwdPassword}
                      onChange={(e) => setPwdPassword(e.target.value)}
                      disabled={pwdSubmitting}
                      data-testid="signin-password-input"
                      autoComplete="current-password"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={pwdSubmitting || !pwdEmail || !pwdPassword}
                    data-testid="signin-submit-btn"
                    className="w-full bg-[#0D9488] hover:bg-[#14B8A6] text-white font-semibold py-3 h-auto"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    {pwdSubmitting ? "Signing in…" : "Sign in"}
                  </Button>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setForgotOpen((v) => !v)}
                      className="text-xs text-slate-500 hover:text-[#0D9488] underline"
                      data-testid="forgot-password-toggle"
                    >
                      Forgot password?
                    </button>
                  </div>
                  {forgotOpen && (
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 space-y-2" data-testid="forgot-panel">
                      <p>Enter your email above, then click below — we&apos;ll send you a reset link.</p>
                      <Button
                        type="button"
                        onClick={handleForgotPassword}
                        disabled={forgotSending || !pwdEmail}
                        variant="outline"
                        className="w-full h-9 text-xs"
                        data-testid="forgot-submit-btn"
                      >
                        {forgotSending ? "Sending…" : "Send reset link"}
                      </Button>
                    </div>
                  )}
                </form>

                <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-slate-400">
                  <span className="h-px bg-slate-200 flex-1" />
                  <span>or</span>
                  <span className="h-px bg-slate-200 flex-1" />
                </div>

                <Button
                  onClick={handleLogin}
                  data-testid="auth-google-btn"
                  className="w-full bg-[#0A192F] hover:bg-[#112240] text-white font-semibold py-3 h-auto"
                >
                  Continue with Google
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>

                <form onSubmit={handleMagicLink} className="space-y-2">
                  <Input
                    id="magic-email"
                    type="email"
                    placeholder="Email for magic-link sign-in"
                    value={magicEmail}
                    onChange={(e) => setMagicEmail(e.target.value)}
                    disabled={magicSending}
                    data-testid="magic-link-email-input"
                    autoComplete="email"
                  />
                  <Button
                    type="submit"
                    disabled={magicSending || !magicEmail}
                    data-testid="magic-link-submit-btn"
                    variant="outline"
                    className="w-full font-medium py-3 h-auto"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {magicSending ? "Sending link…" : "Email me a sign-in link"}
                  </Button>
                </form>

                {/* Phone OTP sign-in (P3) — clicking the toggle swaps the
                    entire signin surface to the phone flow (above), hiding
                    password/Google/magic-link so the UI is dedicated. */}
                <div className="pt-1">
                  <button
                    type="button"
                    onClick={() => setPhoneOpen(true)}
                    className="w-full text-xs text-slate-500 hover:text-[#0D9488] underline flex items-center justify-center gap-1.5"
                    data-testid="phone-signin-toggle"
                  >
                    <Smartphone className="w-3.5 h-3.5" />
                    No email? Sign in with your phone number
                  </button>
                </div>
                  </>
                )}
              </TabsContent>

              {/* ── Create account tab ───────────────────────────────── */}
              <TabsContent value="signup" className="space-y-4 pt-4">
                {/* Plan picker — hidden when the user already committed to
                    a plan from the PricingGrid on the landing page. */}
                {!pendingCheckoutIntent && (
                  <div className="space-y-2" data-testid="signup-plan-picker">
                    <label className="block text-xs font-medium text-slate-600">
                      Choose your plan
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {livePlans.map((p) => {
                        const active = signupPlan === p.id;
                        const isFree = p.is_free || p.price === 0;
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => setSignupPlan(p.id)}
                            data-testid={`signup-plan-${p.id}`}
                            className={`relative rounded-md border px-2 py-2 text-left transition ${
                              active
                                ? "border-[#0D9488] bg-teal-50 ring-1 ring-[#0D9488]"
                                : "border-slate-200 hover:border-slate-300 bg-white"
                            }`}
                          >
                            {p.popular && (
                              <span className="absolute -top-1.5 right-1 bg-[#0D9488] text-white text-[9px] font-semibold px-1.5 py-0.5 rounded">
                                Popular
                              </span>
                            )}
                            <div className="text-[11px] font-semibold text-slate-700">{p.name}</div>
                            <div className="text-sm font-bold text-slate-900 leading-tight">
                              ${p.price}
                              <span className="text-[10px] text-slate-500 font-normal">
                                {isFree ? "" : "/mo"}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {signupPlan !== "free" && (
                      <p className="text-[11px] text-slate-500">
                        After creating your account we&apos;ll take you to Stripe to activate <span className="font-medium capitalize">{signupPlan}</span>.
                      </p>
                    )}
                  </div>
                )}
                <form onSubmit={handlePasswordSignUp} className="space-y-3" data-testid="signup-form">
                  <div className="space-y-1.5">
                    <label htmlFor="signup-email" className="block text-xs font-medium text-slate-600">Email</label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="you@company.com"
                      value={pwdEmail}
                      onChange={(e) => setPwdEmail(e.target.value)}
                      disabled={pwdSubmitting}
                      data-testid="signup-email-input"
                      autoComplete="email"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="signup-password" className="flex items-center justify-between text-xs font-medium text-slate-600">
                      <span>Password (min 8 characters)</span>
                      <button
                        type="button"
                        onClick={() => setShowPwd((v) => !v)}
                        className="text-slate-400 hover:text-slate-600 inline-flex items-center gap-1"
                        data-testid="signup-show-password"
                      >
                        {showPwd ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        {showPwd ? "Hide" : "Show"}
                      </button>
                    </label>
                    <Input
                      id="signup-password"
                      type={showPwd ? "text" : "password"}
                      placeholder="Choose a strong password"
                      value={pwdPassword}
                      onChange={(e) => setPwdPassword(e.target.value)}
                      disabled={pwdSubmitting}
                      data-testid="signup-password-input"
                      autoComplete="new-password"
                      minLength={8}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="signup-company" className="block text-xs font-medium text-slate-600">Company name</label>
                    <Input
                      id="signup-company"
                      type="text"
                      placeholder="Acme Restaurant Group"
                      value={pwdCompany}
                      onChange={(e) => setPwdCompany(e.target.value)}
                      disabled={pwdSubmitting}
                      data-testid="signup-company-input"
                      autoComplete="organization"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="signup-name" className="block text-xs font-medium text-slate-600">Your name <span className="text-slate-400">(optional)</span></label>
                    <Input
                      id="signup-name"
                      type="text"
                      placeholder="Jane Owner"
                      value={pwdName}
                      onChange={(e) => setPwdName(e.target.value)}
                      disabled={pwdSubmitting}
                      data-testid="signup-name-input"
                      autoComplete="name"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={pwdSubmitting || !pwdEmail || !pwdPassword || !pwdCompany}
                    data-testid="signup-submit-btn"
                    className="w-full bg-[#0D9488] hover:bg-[#14B8A6] text-white font-semibold py-3 h-auto"
                  >
                    {pwdSubmitting
                      ? "Creating account…"
                      : pendingCheckoutIntent
                        ? `Create account & continue to ${pendingCheckoutIntent.plan === "pro" ? "Pro" : "Starter"} checkout`
                        : signupPlan !== "free"
                          ? `Create account & continue to ${signupPlan === "pro" ? "Pro" : "Starter"} checkout`
                          : "Create free account"}
                  </Button>
                  <p className="text-[11px] text-slate-400 text-center">
                    By creating an account you agree to our Terms &amp; Privacy Policy.
                  </p>
                </form>

                <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-slate-400">
                  <span className="h-px bg-slate-200 flex-1" />
                  <span>or</span>
                  <span className="h-px bg-slate-200 flex-1" />
                </div>

                <Button
                  onClick={handleLogin}
                  data-testid="signup-google-btn"
                  variant="outline"
                  className="w-full font-medium py-3 h-auto"
                >
                  Sign up with Google
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Demo "Coming Soon" modal */}
      <Dialog open={demoOpen} onOpenChange={setDemoOpen}>
        <DialogContent data-testid="demo-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold tracking-tight flex items-center gap-2">
              <PlayCircle className="w-6 h-6 text-[#0D9488]" />
              Product demo
            </DialogTitle>
            <DialogDescription>
              We&apos;re putting together a walkthrough video right now.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="aspect-video w-full rounded-lg bg-gradient-to-br from-[#0A192F] to-[#112240] border border-slate-200 flex flex-col items-center justify-center text-center px-6">
              <PlayCircle className="w-16 h-16 text-[#0D9488] mb-3 opacity-80" />
              <p className="text-white font-semibold">Coming soon</p>
              <p className="text-xs text-slate-400 mt-1">A 2-minute tour of Nirvanta — released this month.</p>
            </div>
            <p className="text-sm text-slate-600 text-center">
              In the meantime, the fastest way to see it is the free trial — set up takes under 2 minutes.
            </p>
            <Button
              onClick={() => { setDemoOpen(false); setAuthOpen(true); }}
              data-testid="demo-start-trial-btn"
              className="w-full bg-[#0D9488] hover:bg-[#14B8A6] text-white font-semibold py-3 h-auto"
            >
              Start Free Trial instead
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Features Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A192F] mb-4 font-['Manrope']">
              Everything You Need to Manage Workforce Pay
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From time tracking to payment recording, Nirvanta handles the complexity so you can focus on your business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-slate-50 rounded-lg p-6 border border-slate-200 hover:shadow-md hover:border-slate-300 transition-all duration-200"
              >
                <div className="w-12 h-12 bg-[#CCFBF1] rounded-lg flex items-center justify-center text-[#0D9488] mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-[#0A192F] mb-2 font-['Manrope']">
                  {feature.title}
                </h3>
                <p className="text-slate-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A192F] mb-6 font-['Manrope']">
                Built for Operational Efficiency
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Nirvanta focuses on operational payroll tracking—not tax compliance. Perfect for businesses that need to track pay, handle delayed payments, and manage workforce schedules.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#0D9488] flex-shrink-0" />
                    <span className="text-slate-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-8 border border-slate-200">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <span className="text-slate-600">Total Outstanding</span>
                  <span className="text-2xl font-bold text-[#E11D48]">$4,250.00</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <span className="text-slate-600">Paid This Month</span>
                  <span className="text-2xl font-bold text-[#10B981]">$12,500.00</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <span className="text-slate-600">Active Employees</span>
                  <span className="text-2xl font-bold text-[#0A192F]">12</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div id="pricing" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A192F] mb-4 font-['Manrope']">
              Simple, transparent pricing
            </h2>
            <p className="text-lg text-slate-500">Start free. Upgrade when your team grows.</p>
          </div>
          <PricingGrid onSelectFree={handleLogin} ctaSize="lg" />
          <p className="text-center text-sm text-slate-400 mt-8">Starter &amp; Pro include unlimited Time Clock kiosks · No credit card needed for Free.</p>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-[#0A192F]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-6 font-['Manrope']">
            Ready to Simplify Your Workforce Pay?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Get started in minutes. No credit card required.
          </p>
          <Button 
            onClick={handleLogin}
            data-testid="cta-signup-btn"
            className="bg-[#0D9488] hover:bg-[#14B8A6] text-white font-semibold px-8 py-3 text-lg"
          >
            Start Free Trial
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#112240] text-slate-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#0D9488] rounded-lg flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-bold text-white font-['Manrope']">Nirvanta</span>
            </div>
            <p className="text-sm">
              © 2025 Marai Software Tech. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
